import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { createStyles } from 'utils/createStyle';
import { selectSelectedOrgId } from 'common/features/store/duck/home/duck';
import { useConfigureUrlWithAffiliateToken } from 'common/hooks/useGetAffiliateActivationUrl';
import { AffiliateQueryParams } from 'common/api/crm/enums/Affiliate';
import { logAffiliateClick } from 'config/analytics';
import {
    selectIsFlipgiveEnabledWeb,
    selectIsAffiliateConfirmationWebEnabled,
} from 'common/features/store/duck/ffs';
import { useRootSelector } from 'common/features/featuresReducer';
import { getConfirmationDrawerText } from 'common/utils/confirmationDrawer';
import AffiliateConfirmationModal from 'features/product/components/AffiliateConfirmationModal';
import { getBrand } from 'common/api/e-comm/brands';
import { AffiliateOffer } from 'common/features/store/duck/wallet/duck';

const styles = createStyles({
    shopOnlineBanner: [
        'flex flex-col text-brand-dark bg-pale-blue font-semibold mt-6 pt-6 gap-4 px-5 items-center justify-center text-center',
        {
            borderRadius: '10px',
            fontSize: '18px',
            lineHeight: '25px',
            '@media (min-width: 640px)': {
                fontSize: '20px',
                lineHeight: '28px',
            },
        },
    ],
    shopNowButton: [
        'mt-0 md:px-16 sm:px-24 py-2.5 w-full sm:w-auto flex items-center justify-center rounded-3xl font-semibold no-underline',
        { fontSize: '16px' },
    ],
    errorMessage: ['text-error font-normal', { fontSize: '12px', lineHeight: '18px' }],
    message: [{ whiteSpace: 'normal', wordWrap: 'break-word', wordBreak: 'break-word' }],
});

export const ShopOnlineBanner = ({
    brandId,
    affiliateOffer,
    atRaiseRight,
}: {
    brandId: number;
    affiliateOffer: AffiliateOffer;
    atRaiseRight: boolean;
}) => {
    const showAffiliateOffersForWallet = useRootSelector(selectIsFlipgiveEnabledWeb);

    const [activationUrl, isLoadingUrl] = useConfigureUrlWithAffiliateToken(
        '{{token}}',
        affiliateOffer?.url,
        AffiliateQueryParams.ChannelWeb.replace('{source}', 'wallet')
    );

    const isFetchingOrError = isLoadingUrl || !activationUrl || !showAffiliateOffersForWallet;

    const isAffiliateConfirmationEnabled = useSelector(selectIsAffiliateConfirmationWebEnabled);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const orgId = useSelector(selectSelectedOrgId);

    const [brandLogo, setBrandLogo] = useState<string>();

    useEffect(() => {
        (async () => {
            const _brand = await getBrand(brandId);
            setBrandLogo(_brand.data?.imageUrl);
        })();
    }, [brandId]);

    const rebateMessage = `${affiliateOffer.variable ? 'up to' : ''} ${
        affiliateOffer.bestRebate
    }% more`;

    const onClickShopNow = useCallback(() => {
        logAffiliateClick({
            affiliateChannel: process.env.REACT_APP_RAISE_RIGHT_APP_TYPE || 'web',
            affiliateMobilePlatform: navigator.userAgent.match(/iPhone/i)
                ? 'ios'
                : navigator.userAgent.match(/Android/i)
                ? 'android'
                : '',
            version: `react-web-${process.env.REACT_APP_VERSION}`,
            location: atRaiseRight ? 'wallet' : 'order summary',
            brandId,
            brandName: affiliateOffer!.name,
            orgId,
        });

        if (isAffiliateConfirmationEnabled) {
            setIsDrawerOpen(true);
        }
    }, [affiliateOffer, atRaiseRight, brandId, isAffiliateConfirmationEnabled, orgId]);

    const timeframeMessage = getConfirmationDrawerText(
        affiliateOffer?.averagePurchaseDelayHours ?? 0
    );

    if (!affiliateOffer?.canDoubleDip) {
        return null;
    }

    return (
        <div
            className={`${!activationUrl && !isLoadingUrl ? 'pb-4' : 'p-8'} ${
                styles.shopOnlineBanner
            }`}
        >
            {atRaiseRight ? (
                <span>{`Shop online through RaiseRight and earn ${rebateMessage}`}</span>
            ) : (
                <span className={styles.message}>
                    {`Shop online at `}
                    <span className="whitespace-nowrap">{affiliateOffer.name}</span>
                    {` and earn ${rebateMessage}`}
                </span>
            )}
            <a
                className={`${styles.shopNowButton} ${
                    !isFetchingOrError
                        ? 'text-white bg-brand'
                        : 'text-grey-medium bg-grey-light pointer-events-none'
                }`}
                onClick={onClickShopNow}
                target="_blank"
                href={!isFetchingOrError ? activationUrl : '#'}
            >
                Shop Now{' '}
                <ExternalLinkArrow
                    className="inline ml-2 w-3 h-5"
                    color={!isFetchingOrError ? '#fff' : '#6D6E71'}
                />
            </a>
            {!showAffiliateOffersForWallet || (!activationUrl && !isLoadingUrl) ? (
                <span className={styles.errorMessage}>
                    We’re sorry, shopping online is currently unavailable. Please try again later.
                </span>
            ) : null}
            {isAffiliateConfirmationEnabled && (
                <AffiliateConfirmationModal
                    brandName={affiliateOffer.name}
                    timeframeMessage={timeframeMessage}
                    earningsPercentage={
                        affiliateOffer.variable
                            ? `up to ${affiliateOffer.bestRebate}%`
                            : `${affiliateOffer.bestRebate}%`
                    }
                    brandLogo={brandLogo || ''}
                    isOpen={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                />
            )}
        </div>
    );
};

export const ExternalLinkArrow = (props: any) => (
    <svg width="11" height="10" viewBox="0 0 11 10" fill="none" {...props}>
        <path
            d="M10.2295 0.75V8.25C10.2295 8.6875 9.88574 9 9.47949 9C9.04199 9 8.72949 8.6875 8.72949 8.25V2.5625L1.51074 9.78125C1.35449 9.9375 1.16699 10 0.979492 10C0.760742 10 0.573242 9.9375 0.448242 9.78125C0.135742 9.5 0.135742 9.03125 0.448242 8.75L7.66699 1.5H1.97949C1.54199 1.5 1.22949 1.1875 1.22949 0.75C1.22949 0.34375 1.54199 0 1.97949 0H9.47949C9.88574 0 10.2295 0.34375 10.2295 0.75Z"
            fill={props.color}
        />
    </svg>
);
